import React, { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Switch,
  Table,
  Upload,
} from "antd";
import Flex from "../../../components/shared-components/Flex";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { addDoc, collection, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { useAuth } from "../../../data/hooks/firebase/useAuth";
import EllipsisDropdown from "../../../components/shared-components/EllipsisDropdown";
import { FIRESTORE_NOTIFICATIONS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { firestore, storage } from "../../../configs/FirebaseConfig";
import i18n from "../../../translations/i18n";
import ImgCrop from "antd-img-crop";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import styled from "styled-components";
import locale from "antd/es/date-picker/locale/nl_NL";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import dayjs from "dayjs";
import { dummyRequest } from "../../../functions/browser";
import { useNotifications } from "../../../data/hooks/firestore/useNotifications";
import { useProducts } from "../../../data/hooks/firestore/useProducts";

const { TextArea } = Input;
const { Option } = Select;

const ADD = "ADD";
const EDIT = "EDIT";
const imagesPath = "notifications";
const NOTIFICATION_DATE_FORMAT = "YYYY-MM-DD";

const Notifications = () => {
  const { t } = useTranslation();
  const { data: notifications, loading } = useNotifications();
  const { products, isLoading: productsLoading } = useProducts();
  const { email } = useAuth();
  const [addNotificationForm] = Form.useForm();
  const [editNotificationForm] = Form.useForm();
  const ASPECT_RATIO_IMAGE = 4 / 3;
  const [addNotificationModalVisible, setAddNotificationModalVisible] = useState(false);
  const [editNotificationModalVisible, setEditNotificationModalVisible] = useState(false);
  const [notificationToEdit, setNotificationToEdit] = useState(null);
  const [uploadedImageNl, setUploadedImageNl] = useState(null);
  const [uploadedImageEn, setUploadedImageEn] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [startDate, setStartDate] = useState(null);

  const openEditModal = (notification) => {
    setNotificationToEdit(notification);
    editNotificationForm.setFieldsValue({
      title_nl: notification.titleNL,
      title_en: notification.titleEN,
      description_nl: notification.descriptionNL,
      description_en: notification.descriptionEN,
      start_date: dayjs(notification.startDate, "YYYY-MM-DD"),
      expiration_date: dayjs(notification.endDate, "YYYY-MM-DD"),
      link: notification.link || null,
    });
    setEditNotificationModalVisible(true);
    setIsActive(notification.active);
  };

  const deleteNotification = async (notification) => {
    const imageRefNL = !!notification.imageNL ? ref(storage, notification.imageNL) : null;
    const imageRefEN = !!notification.imageEN ? ref(storage, notification.imageEN) : null;
    if (!!imageRefNL) deleteObject(imageRefNL).catch(console.error);
    if (!!imageRefEN) deleteObject(imageRefEN).catch(console.error);
    try {
      await deleteDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_NOTIFICATIONS_TABLE, notification.id),
      );
      message.success(t("notifications.notification_deleted"));
    } catch (error) {
      console.error("Error deleting notification:", error);
      message.error(t("notifications.error_deleting_notification"));
    }
  };

  const closeAddModal = () => {
    setAddNotificationModalVisible(false);
    setUploadedImageNl(null);
    setUploadedImageEn(null);
    setIsActive(true);
    setStartDate(null);
    addNotificationForm.resetFields();
  };

  const closeEditModal = () => {
    setEditNotificationModalVisible(false);
    setNotificationToEdit(null);
    setIsActive(true);
    setStartDate(null);
    editNotificationForm.resetFields();
  };

  const handleUpdateNotification = () => {
    editNotificationForm.validateFields().then(async (values) => {
      let object = setNotificationLoggingProperties(
        {
          titleNL: values.title_nl,
          titleEN: values.title_en,
          descriptionNL: values.description_nl,
          descriptionEN: values.description_en,
          startDate: dayjs(values.start_date).format(NOTIFICATION_DATE_FORMAT),
          endDate: dayjs(values.expiration_date).format(NOTIFICATION_DATE_FORMAT),
          active: isActive,
          link: values.link || null,
        },
        EDIT,
      );

      try {
        await updateDoc(
          doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_NOTIFICATIONS_TABLE,
            notificationToEdit.id,
          ),
          object,
        );
        closeEditModal();
        message.success(t("notifications.notification_saved"), 2);
      } catch (error) {
        console.error("Error updating notification:", error);
        message.error(t("notifications.error_updating_notification"));
      }
    });
  };

  const setNotificationLoggingProperties = (notification, mode) => {
    if (mode === ADD) return { ...notification, createdAt: dayjs().unix(), createdBy: email };
    else if (mode === EDIT) return { ...notification, updatedAt: dayjs().unix(), updatedBy: email };
  };

  const handleAddNotification = () => {
    addNotificationForm.validateFields().then((values) => {
      let object = setNotificationLoggingProperties(
        {
          titleNL: values.title_nl,
          titleEN: values.title_en,
          descriptionNL: values.description_nl,
          descriptionEN: values.description_en,
          startDate: dayjs(values.start_date).format(NOTIFICATION_DATE_FORMAT),
          endDate: dayjs(values.expiration_date).format(NOTIFICATION_DATE_FORMAT),
          active: isActive,
          link: values.link || null,
          imageNL: null,
          imageEN: null,
          hasSeen: [],
        },
        ADD,
      );
      const imageNameNL = !!uploadedImageNl ? uploadedImageNl.uid + "-" + uploadedImageNl.name : null;
      const imageNameEN = !!uploadedImageEn ? uploadedImageEn.uid + "-" + uploadedImageEn.name : null;
      const images = [];
      if (!!uploadedImageNl) images.push({ name: imageNameNL, file: uploadedImageNl.originFileObj, lang: "NL" });
      if (!!uploadedImageEn) images.push({ name: imageNameEN, file: uploadedImageEn.originFileObj, lang: "EN" });

      Promise.all(
        // Array of "Promises"
        images.map((item) => putStorageItem(item)),
      )
        .then(async (urls) => {
          urls.forEach((item) => {
            const objectVariable = `image${[item.lang]}`;
            object[objectVariable] = item.url;
          });
          try {
            await addDoc(
              collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_NOTIFICATIONS_TABLE),
              object,
            );
            closeAddModal();
          } catch (err) {
            console.error("Error adding notification:", err);
            message.error(err.message);
          }
        })
        .catch((error) => console.log(error.message));
    });
  };

  const putStorageItem = (item) => {
    return new Promise((resolve, reject) => {
      const imageRef = ref(storage, imagesPath + "/" + item.name);
      uploadBytes(imageRef, item.file)
        .then((snapshot) => {
          getDownloadURL(snapshot.ref).then((url) => {
            resolve({ lang: item.lang, url: url });
          });
        })
        .catch((error) => {
          reject(item, error.message);
        });
    });
  };

  const getActiveStatus = (notification) => {
    const today = dayjs();
    const isBetween = today.isBetween(
      dayjs(notification.startDate, NOTIFICATION_DATE_FORMAT),
      dayjs(notification.endDate, NOTIFICATION_DATE_FORMAT),
      undefined,
      "[]",
    );
    if (!notification.active)
      return (
        <>
          <Badge status="warning" />
          <span style={{ marginLeft: "6px" }}>{t("inactive")}</span>
        </>
      );
    if (!isBetween)
      return (
        <>
          <Badge status="warning" />
          <span style={{ marginLeft: "6px" }}>{t("inactive_period")}</span>
        </>
      );
    else
      return (
        <>
          <Badge status="success" />
          <span style={{ marginLeft: "6px" }}>{t("active")}</span>
        </>
      );
  };

  const tableColumns = [
    {
      title: t("title"),
      render: (_, data) => <p style={{ whiteSpace: "pre-line" }}>{"NL: " + data.titleNL + "\nEN: " + data.titleEN}</p>,
    },
    {
      title: t("description"),
      render: (_, data) => (
        <p style={{ whiteSpace: "pre-line" }}>{"NL: " + data.descriptionNL + "\nEN: " + data.descriptionEN}</p>
      ),
    },
    {
      title: t("link"),
      dataIndex: "link",
    },
    {
      title: t("start_date"),
      dataIndex: "startDate",
    },
    {
      title: t("end_date"),
      dataIndex: "endDate",
    },
    {
      title: t("image"),
      dataIndex: "imageNL",
      render: (_, data) => (
        <div>
          <img src={data.imageNL} alt={""} style={{ maxWidth: 100, maxHeight: 80 }} />
          <img src={data.imageEN} alt={""} style={{ maxWidth: 100, maxHeight: 80 }} />
        </div>
      ),
    },
    {
      title: t("status"),
      render: (_) => <Flex alignItems="center">{getActiveStatus(_)}</Flex>,
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, row) => (
        <div className="text-right">
          <EllipsisDropdown
            menu={
              <Menu>
                <Menu.Item onClick={() => openEditModal(row)}>
                  <Flex alignItems="center">
                    <EditOutlined />
                    <span style={{ marginLeft: 4 }}>{t("edit")}</span>
                  </Flex>
                </Menu.Item>
                <Menu.Item>
                  <Flex alignItems="center">
                    <DeleteOutlined />
                    <Popconfirm
                      placement="bottom"
                      title={t("delete_notification?")}
                      onConfirm={() => deleteNotification(row)}
                      okText={t("yes")}
                      cancelText={t("no")}
                    >
                      <span style={{ marginLeft: 4 }}>{t("delete")}</span>
                    </Popconfirm>
                  </Flex>
                </Menu.Item>
              </Menu>
            }
          />
        </div>
      ),
    },
  ];

  const onChangeImage = (file, lang) => {
    const status = file.status;
    if (lang === "nl") {
      if (status === "done") setUploadedImageNl(file);
      if (status === "removed") setUploadedImageNl(null);
    } else {
      if (status === "done") setUploadedImageEn(file);
      if (status === "removed") setUploadedImageEn(null);
    }
  };

  /**
   * When file is uploaded, check for file type and image size
   * @param {*} file The uploaded file
   * @returns Boolean that tells Upload component whether to add image or not
   */
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error(i18n.t("notifications.only_png_jpg"));
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(i18n.t("notifications.image_max_size"));
      return Upload.LIST_IGNORE;
    }
    return isJpgOrPng && isLt2M;
  };

  return (
    <Card>
      <Flex alignItems="between" justifyContent="between" mobileFlex={false}>
        <Flex alignItems="end" className="mb-1" mobileFlex={false}>
          <div>
            <Button
              onClick={() => setAddNotificationModalVisible(true)}
              type="primary"
              icon={<PlusCircleOutlined />}
              block
            >
              {t("add_notification")}
            </Button>
          </div>
        </Flex>
      </Flex>
      <div className="table-responsive">
        <Table
          columns={tableColumns}
          dataSource={notifications}
          rowKey="id"
          scroll={{ x: 1300 }}
          loading={loading || productsLoading}
        />
      </div>
      <Modal
        title={t("add_notification")}
        open={addNotificationModalVisible}
        onCancel={() => closeAddModal()}
        onOk={handleAddNotification}
        okText={t("save")}
        cancelText={t("close")}
      >
        <Form layout="vertical" form={addNotificationForm} name="advanced_search" className="ant-advanced-search-form">
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item
                name="title_nl"
                label={t("title_nl")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_title"),
                  },
                ]}
              >
                <Input placeholder={t("title_nl")} />
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item
                name="title_en"
                label={t("title_en")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_title"),
                  },
                ]}
              >
                <Input placeholder={t("title_en")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item
                name="description_nl"
                label={t("description_nl")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_description"),
                  },
                ]}
              >
                <TextArea placeholder={t("description_nl")} />
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item
                name="description_en"
                label={t("description_en")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_description"),
                  },
                ]}
              >
                <TextArea placeholder={t("description_en")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item
                name="start_date"
                label={t("start_date")}
                rules={[
                  {
                    required: true,
                    message: t("form.choose_start_date"),
                  },
                ]}
              >
                <DatePicker
                  locale={locale}
                  format={NOTIFICATION_DATE_FORMAT}
                  placeholder={t("start_date_placeholder")}
                  disabledDate={(current) => {
                    return current < dayjs().startOf("day");
                  }}
                  style={{ width: "100%" }}
                  onChange={(value) => {
                    setStartDate(value);
                    let expDate = addNotificationForm.getFieldValue("expiration_date");
                    if (!!expDate && expDate < value) addNotificationForm.resetFields(["expiration_date"]);
                  }}
                />
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item
                name="expiration_date"
                label={t("expiration_date")}
                rules={[
                  {
                    required: true,
                    message: t("form.choose_expiration_date"),
                  },
                ]}
              >
                <DatePicker
                  locale={locale}
                  format={NOTIFICATION_DATE_FORMAT}
                  placeholder={t("expiration_date_placeholder")}
                  disabledDate={(current) => {
                    return current < dayjs().endOf("day") || (!!startDate && current < startDate.endOf("day"));
                  }}
                  showToday={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={"image_nl"}
                label={t("notification_image_nl")}
                tooltip={{ title: t("notification_image_tooltip"), icon: <InfoCircleOutlined /> }}
              >
                {!uploadedImageNl ? (
                  <ImgCrop
                    modalWidth={1000}
                    minZoom={0}
                    modalTitle={t("crop_image")}
                    modalOk={t("add")}
                    modalCancel={t("close")}
                    aspect={ASPECT_RATIO_IMAGE}
                    cropperProps={{
                      restrictPosition: false,
                    }}
                  >
                    <Upload
                      beforeUpload={beforeUpload}
                      onChange={(e) => onChangeImage(e.file, "nl")}
                      customRequest={dummyRequest}
                      maxCount={1}
                      multiple={false}
                      showUploadList={false}
                    >
                      <Button icon={<UploadOutlined />}>{t("upload")}</Button>
                    </Upload>
                  </ImgCrop>
                ) : (
                  <ImageFileWrapper>
                    <p>{uploadedImageNl.name}</p>
                    <DeleteOutlined onClick={() => setUploadedImageNl(null)} />
                  </ImageFileWrapper>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"image_en"}
                label={t("notification_image_en")}
                tooltip={{ title: t("notification_image_tooltip"), icon: <InfoCircleOutlined /> }}
              >
                {!uploadedImageEn ? (
                  <ImgCrop
                    modalWidth={1000}
                    minZoom={0}
                    modalTitle={t("crop_image")}
                    modalOk={t("add")}
                    modalCancel={t("close")}
                    aspect={ASPECT_RATIO_IMAGE}
                    cropperProps={{
                      restrictPosition: false,
                    }}
                  >
                    <Upload
                      beforeUpload={beforeUpload}
                      onChange={(e) => onChangeImage(e.file, "en")}
                      customRequest={dummyRequest}
                      maxCount={1}
                      multiple={false}
                    >
                      <Button icon={<UploadOutlined />}>{t("upload")}</Button>
                    </Upload>
                  </ImgCrop>
                ) : (
                  <ImageFileWrapper>
                    <p>{uploadedImageEn.name}</p>
                    <DeleteOutlined onClick={() => setUploadedImageEn(null)} />
                  </ImageFileWrapper>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item
                name={"link"}
                label={t("link")}
                tooltip={{ title: t("link_tooltip"), icon: <InfoCircleOutlined /> }}
              >
                <Select showSearch optionFilterProp="children" allowClear placeholder={t("form.choose_product")}>
                  {products.map((product) => (
                    <Option key={product.productId} value={product.productId}>
                      {product.productNameNL}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item
                name={"active"}
                label={t("active")}
                tooltip={{ title: t("notification_active_tooltip"), icon: <InfoCircleOutlined /> }}
              >
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  checked={isActive}
                  onChange={setIsActive}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {editNotificationModalVisible && (
        <Modal
          title={t("edit_notification")}
          open={editNotificationModalVisible}
          onCancel={() => closeEditModal()}
          onOk={handleUpdateNotification}
          okText={t("save")}
          cancelText={t("close")}
        >
          <Form
            layout="vertical"
            form={editNotificationForm}
            name="advanced_search"
            className="ant-advanced-search-form"
          >
            <Row gutter={16}>
              <Col lg={12}>
                <Form.Item
                  name="title_nl"
                  label={t("title_nl")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_title"),
                    },
                  ]}
                >
                  <Input placeholder={t("title_nl")} />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  name="title_en"
                  label={t("title_en")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_title"),
                    },
                  ]}
                >
                  <Input placeholder={t("title_en")} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col lg={12}>
                <Form.Item
                  name="description_nl"
                  label={t("description_nl")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_description"),
                    },
                  ]}
                >
                  <TextArea placeholder={t("description_nl")} />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  name="description_en"
                  label={t("description_en")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_description"),
                    },
                  ]}
                >
                  <TextArea placeholder={t("description_en")} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col lg={12}>
                <Form.Item
                  name="start_date"
                  label={t("start_date")}
                  rules={[
                    {
                      required: true,
                      message: t("form.choose_start_date"),
                    },
                  ]}
                >
                  <DatePicker
                    locale={locale}
                    format={NOTIFICATION_DATE_FORMAT}
                    placeholder={t("start_date_placeholder")}
                    disabledDate={(current) => {
                      return current < dayjs().startOf("day");
                    }}
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      setStartDate(value);
                      let expDate = editNotificationForm.getFieldValue("expiration_date");
                      if (!!expDate && expDate < value) editNotificationForm.resetFields(["expiration_date"]);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  name="expiration_date"
                  label={t("expiration_date")}
                  rules={[
                    {
                      required: true,
                      message: t("form.choose_expiration_date"),
                    },
                  ]}
                >
                  <DatePicker
                    locale={locale}
                    format={NOTIFICATION_DATE_FORMAT}
                    placeholder={t("expiration_date_placeholder")}
                    disabledDate={(current) => {
                      return current < dayjs().endOf("day") || (!!startDate && current < startDate.endOf("day"));
                    }}
                    showToday={false}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={"image_nl"} label={t("notification_image_nl")}>
                  <PreviewImage src={notificationToEdit.imageNL} alt={""} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={"image_en"} label={t("notification_image_en")}>
                  <PreviewImage src={notificationToEdit.imageEN} alt={""} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col lg={12}>
                <Form.Item
                  name={"link"}
                  label={t("link")}
                  tooltip={{ title: t("link_tooltip"), icon: <InfoCircleOutlined /> }}
                >
                  <Select showSearch optionFilterProp="children" allowClear placeholder={t("form.choose_product")}>
                    {products.map((product) => (
                      <Option key={product.productId} value={product.productId}>
                        {product.productNameNL}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  name={"active"}
                  label={t("active")}
                  tooltip={{ title: t("notification_active_tooltip"), icon: <InfoCircleOutlined /> }}
                >
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    checked={isActive}
                    onChange={setIsActive}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      )}
    </Card>
  );
};

const ImageFileWrapper = styled.div`
  display: flex;
  transition: all 0.3s ease;
  padding: 0.2rem 0.3rem;
  align-items: center;

  p {
    margin-right: 1rem;
    margin-bottom: 0;
  }

  span {
    &:hover {
      cursor: pointer;
    }
  }

  &:hover {
    background-color: #f5f5f5;
  }
`;

const PreviewImage = styled.img`
  width: fill-available;
`;

export default Notifications;
